(function () {
class PlatefulCountdown extends HTMLElement {
  connectedCallback() {
    this.output = this.querySelector('[data-countdown-output]');
    const storedKey = `plateful-countdown-${this.dataset.durationMinutes || 120}`;
    const suppliedDate = this.dataset.endDate ? new Date(this.dataset.endDate) : null;
    let endTimestamp = suppliedDate && !Number.isNaN(suppliedDate.getTime()) ? suppliedDate.getTime() : 0;

    if (!endTimestamp) {
      endTimestamp = Number(window.sessionStorage.getItem(storedKey));
      if (!endTimestamp || endTimestamp <= Date.now()) {
        endTimestamp = Date.now() + Number(this.dataset.durationMinutes || 120) * 60000;
        window.sessionStorage.setItem(storedKey, String(endTimestamp));
      }
    }
    this.endDate = new Date(endTimestamp);

    if (!this.output || Number.isNaN(this.endDate.getTime())) {
      this.hidden = true;
      return;
    }

    this.render();
    this.timer = window.setInterval(() => this.render(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  render() {
    const remaining = this.endDate.getTime() - Date.now();

    if (remaining <= 0) {
      window.clearInterval(this.timer);
      this.output.textContent = this.dataset.expiredText || '';
      if (!this.output.textContent) this.hidden = true;
      return;
    }

    const hours = Math.floor(remaining / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    const unit = (value, label) => `<span class="pf-announcement__unit"><strong>${String(value).padStart(2, '0')}</strong><small>${label}</small></span>`;

    this.output.innerHTML = `${unit(hours, 'HRS')}<span class="pf-announcement__divider">:</span>${unit(minutes, 'MINS')}<span class="pf-announcement__divider">:</span>${unit(seconds, 'SECS')}`;
  }
}

if (!customElements.get('plateful-countdown')) {
  customElements.define('plateful-countdown', PlatefulCountdown);
}

const platefulCarouselStates = window.__platefulCarouselStates || new WeakMap();
window.__platefulCarouselStates = platefulCarouselStates;

function measurePlatefulCarousel(carousel) {
  const track = carousel.querySelector('[data-pf-carousel-track]');
  const cards = track ? [...track.querySelectorAll('.pf-product-card')] : [];
  if (!track || !cards.length) return null;
  const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
  const step = cards[0].getBoundingClientRect().width + gap;
  const visible = Math.max(1, Math.floor((track.clientWidth + gap) / step + 0.01));
  return { track, cards, step, visible, maxIndex: Math.max(0, cards.length - visible) };
}

function updatePlatefulCarousel(carousel, requestedIndex, behavior = 'auto') {
  const state = platefulCarouselStates.get(carousel);
  const measurement = measurePlatefulCarousel(carousel);
  if (!state || !measurement) return;
  state.index = Math.max(0, Math.min(requestedIndex, measurement.maxIndex));
  if (behavior) measurement.track.scrollTo({ left: state.index * measurement.step, behavior });
  const previous = carousel.querySelector('[data-pf-carousel-prev]');
  const next = carousel.querySelector('[data-pf-carousel-next]');
  if (previous) {
    previous.disabled = state.index === 0;
    previous.setAttribute('aria-disabled', String(previous.disabled));
  }
  if (next) {
    next.disabled = state.index >= measurement.maxIndex;
    next.setAttribute('aria-disabled', String(next.disabled));
  }
}

function setupPlatefulCarousel(carousel) {
  if (platefulCarouselStates.has(carousel)) return;
  const track = carousel.querySelector('[data-pf-carousel-track]');
  if (!track || !track.querySelector('.pf-product-card')) return;
  const state = { index: 0, initializing: true, scrollFrame: 0, resizeObserver: null };
  platefulCarouselStates.set(carousel, state);
  // Defeat native scroll restoration before it can become carousel state.
  track.scrollLeft = 0;
  track.addEventListener('scroll', () => {
    if (state.initializing) return;
    if (state.scrollFrame) return;
    state.scrollFrame = requestAnimationFrame(() => {
      state.scrollFrame = 0;
      const measurement = measurePlatefulCarousel(carousel);
      if (!measurement) return;
      updatePlatefulCarousel(carousel, Math.round(measurement.track.scrollLeft / measurement.step), null);
    });
  }, { passive: true });
  state.resizeObserver = new ResizeObserver(() => updatePlatefulCarousel(carousel, state.initializing ? 0 : state.index, 'auto'));
  state.resizeObserver.observe(track);
  requestAnimationFrame(() => {
    updatePlatefulCarousel(carousel, 0, 'auto');
    requestAnimationFrame(() => {
      updatePlatefulCarousel(carousel, 0, 'auto');
      state.initializing = false;
    });
  });
}

function setupPlatefulCarousels(scope = document) {
  scope.querySelectorAll('[data-pf-carousel]').forEach(setupPlatefulCarousel);
}

let activePlatefulModal = null;
let platefulModalReturnFocus = null;

function getPlatefulModalFocusableElements(modal) {
  return [...modal.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])')]
    .filter((element) => !element.hidden && element.getClientRects().length > 0);
}

function trapPlatefulModalFocus(event) {
  if (!activePlatefulModal || event.key !== 'Tab') return false;
  const focusable = getPlatefulModalFocusableElements(activePlatefulModal);
  if (!focusable.length) {
    event.preventDefault();
    activePlatefulModal.querySelector('.pf-video-modal__dialog')?.focus({ preventScroll: true });
    return true;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const activeElement = document.activeElement;
  if (event.shiftKey && (activeElement === first || !activePlatefulModal.contains(activeElement))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (activeElement === last || !activePlatefulModal.contains(activeElement))) {
    event.preventDefault();
    first.focus();
  }
  return true;
}

function activatePlatefulFaqTab(tab, moveFocus = false) {
  const section = tab?.closest('[data-pf-faq]');
  if (!section) return;
  const targetPanelId = tab.getAttribute('aria-controls');

  section.querySelectorAll('[data-faq-tab]').forEach((item) => {
    const active = item === tab;
    item.classList.toggle('is-active', active);
    item.setAttribute('aria-selected', String(active));
    item.tabIndex = active ? 0 : -1;
  });

  section.querySelectorAll('[data-faq-panel]').forEach((panel) => {
    const active = panel.id === targetPanelId;
    panel.classList.toggle('is-active', active);
    panel.hidden = !active;
    if (active) {
      panel.querySelectorAll('details').forEach((details, index) => {
        details.open = index === 0;
      });
    }
  });

  if (moveFocus) tab.focus();
}

function selectPlatefulVideo(modal, index) {
  const thumbs = [...modal.querySelectorAll('[data-pf-video-thumb]')];
  const products = [...modal.querySelectorAll('[data-pf-video-product]')];
  const thumb = thumbs[index];
  const player = modal.querySelector('[data-pf-video-player]');
  if (!thumb || !player) return;
  player.pause();
  player.src = thumb.dataset.pfVideoSource;
  player.muted = false;
  player.volume = 1;
  player.load();
  thumbs.forEach((item, itemIndex) => {
    const selected = itemIndex === index;
    item.classList.toggle('is-selected', selected);
    item.setAttribute('aria-pressed', String(selected));
  });
  products.forEach((item, itemIndex) => {
    const selected = itemIndex === index;
    item.classList.toggle('is-selected', selected);
    item.hidden = !selected;
  });
  player.play().catch(() => {
    // Native controls remain available if a browser blocks playback.
  });
}

function openPlatefulVideoModal(opener) {
  const modal = opener.closest('.pf-social-proof')?.querySelector('[data-pf-video-modal]');
  if (!modal) return;
  if (activePlatefulModal && activePlatefulModal !== modal) closePlatefulVideoModal();
  platefulModalReturnFocus = opener;
  activePlatefulModal = modal;
  modal.hidden = false;
  document.body.classList.add('pf-video-modal-open');
  selectPlatefulVideo(modal, Number(opener.dataset.pfVideoIndex) || 0);
  modal.querySelector('[data-pf-video-close]')?.focus({ preventScroll: true });
}

function closePlatefulVideoModal() {
  if (!activePlatefulModal) return;
  const player = activePlatefulModal.querySelector('[data-pf-video-player]');
  player?.pause();
  if (player) {
    player.removeAttribute('src');
    player.load();
  }
  activePlatefulModal.hidden = true;
  document.body.classList.remove('pf-video-modal-open');
  const returnFocus = platefulModalReturnFocus;
  activePlatefulModal = null;
  platefulModalReturnFocus = null;
  if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
}

const platefulCartFeedbackTimers = new WeakMap();

function updatePlatefulCartButton(productForm, state, message = '') {
  const button = productForm?.querySelector('.pf-video-modal__product-button[type="submit"]');
  const label = button?.querySelector('.add-to-cart-text__content > span > span');
  const error = productForm?.closest('[data-pf-video-product]')?.querySelector('[data-pf-product-error]');
  if (!button || !label) return;

  const currentTimer = platefulCartFeedbackTimers.get(productForm);
  if (currentTimer) clearTimeout(currentTimer);
  platefulCartFeedbackTimers.delete(productForm);

  if (state === 'adding') {
    button.disabled = true;
    label.textContent = 'Adding…';
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    const timeout = window.setTimeout(() => updatePlatefulCartButton(productForm, 'error', 'Please try again.'), 15000);
    platefulCartFeedbackTimers.set(productForm, timeout);
    return;
  }

  if (state === 'added') {
    button.disabled = true;
    label.textContent = 'Added ✓';
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    const timeout = window.setTimeout(() => updatePlatefulCartButton(productForm, 'ready'), 1500);
    platefulCartFeedbackTimers.set(productForm, timeout);
    return;
  }

  button.disabled = false;
  label.textContent = 'Add to cart';
  if (error) {
    error.hidden = state !== 'error';
    error.textContent = state === 'error' ? message : '';
  }
}

if (!window.__platefulHomeInteractionsBound) {
  window.__platefulHomeInteractionsBound = true;
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-pf-carousel]').forEach((carousel) => {
      const state = platefulCarouselStates.get(carousel);
      if (!state) return;
      state.initializing = true;
      state.index = 0;
      updatePlatefulCarousel(carousel, 0, 'auto');
      requestAnimationFrame(() => {
        updatePlatefulCarousel(carousel, 0, 'auto');
        state.initializing = false;
      });
    });
  });
  document.addEventListener('click', (event) => {
    const carouselButton = event.target.closest('[data-pf-carousel-prev], [data-pf-carousel-next]');
    if (carouselButton) {
      if (carouselButton.disabled) return;
      const carousel = carouselButton.closest('[data-pf-carousel]');
      const state = carousel && platefulCarouselStates.get(carousel);
      if (carousel && state) updatePlatefulCarousel(carousel, state.index + (carouselButton.hasAttribute('data-pf-carousel-next') ? 1 : -1), 'smooth');
      return;
    }
    const closeButton = event.target.closest('[data-pf-video-close]');
    if (closeButton || (activePlatefulModal && event.target === activePlatefulModal)) {
      closePlatefulVideoModal();
      return;
    }
    const thumbnail = event.target.closest('[data-pf-video-thumb]');
    if (thumbnail) {
      selectPlatefulVideo(thumbnail.closest('[data-pf-video-modal]'), Number(thumbnail.dataset.pfVideoIndex));
      return;
    }
    const videoOpener = event.target.closest('[data-pf-video-open]');
    if (videoOpener) {
      openPlatefulVideoModal(videoOpener);
      return;
    }
    const tab = event.target.closest('[data-faq-tab]');
    if (!tab) return;
    activatePlatefulFaqTab(tab);
  });
  document.addEventListener('submit', (event) => {
    const productForm = event.target.closest?.('.pf-video-modal__product-form');
    if (productForm) updatePlatefulCartButton(productForm, 'adding');
  });
  document.addEventListener('cart:update', (event) => {
    const productForm = event.target.closest?.('.pf-video-modal__product-form');
    if (!productForm || event.detail?.data?.source !== 'product-form-component') return;
    if (event.detail?.data?.didError) {
      updatePlatefulCartButton(productForm, 'error', 'Could not add this item. Please try again.');
      return;
    }
    updatePlatefulCartButton(productForm, 'added');
  });
  document.addEventListener('cart:error', (event) => {
    const productForm = event.target.closest?.('.pf-video-modal__product-form');
    if (!productForm) return;
    const message = event.detail?.data?.message || 'Could not add this item. Please try again.';
    updatePlatefulCartButton(productForm, 'error', message);
  });
  document.addEventListener('keydown', (event) => {
    if (trapPlatefulModalFocus(event)) return;
    if (event.key === 'Escape' && activePlatefulModal) {
      event.preventDefault();
      closePlatefulVideoModal();
      return;
    }
    const tab = event.target.closest?.('[data-faq-tab]');
    if (!tab || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    const tabs = [...tab.closest('[data-pf-faq]').querySelectorAll('[data-faq-tab]')];
    const currentIndex = tabs.indexOf(tab);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    event.preventDefault();
    activatePlatefulFaqTab(tabs[nextIndex], true);
  });
  document.addEventListener('shopify:section:load', (event) => setupPlatefulCarousels(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (activePlatefulModal && event.target.contains(activePlatefulModal)) closePlatefulVideoModal();
    event.target.querySelectorAll('[data-pf-carousel]').forEach((carousel) => {
      const state = platefulCarouselStates.get(carousel);
      state?.resizeObserver?.disconnect();
      if (state?.scrollFrame) cancelAnimationFrame(state.scrollFrame);
      platefulCarouselStates.delete(carousel);
    });
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setupPlatefulCarousels(), { once: true });
else setupPlatefulCarousels();
})();
